import React, { useState, useEffect } from "react";
import { Radio, InputNumber } from "antd";
import { Form } from "formik-antd";
import i18n from "i18n";
import CustomModal from "components/CustomModal";
import type { RadioChangeEvent } from "antd";
import { FortuneSettingsType } from "api/sms-api";

interface EmailOptionModalProps {
  visible: boolean;
  closeModal: () => void;
  onSave: (
    emailDelayMinutes: number,
    fortuneSettingsTypeId: string | null
  ) => void;
  currentEmailDelayMinutes: number;
  currentFortuneSettingsTypeId: string | null;
  fortuneSettingsTypeList: Array<FortuneSettingsType>;
}

const EmailOptionModal: React.FC<EmailOptionModalProps> = ({
  visible,
  closeModal,
  onSave,
  currentEmailDelayMinutes,
  currentFortuneSettingsTypeId,
  fortuneSettingsTypeList,
}) => {
  const [localEmailDelayMinutes, setLocalEmailDelayMinutes] = useState(
    currentEmailDelayMinutes
  );
  const [localFortuneSettingsTypeId, setLocalFortuneSettingsTypeId] = useState<
    string | null
  >(
    currentFortuneSettingsTypeId ||
      (fortuneSettingsTypeList.length > 0
        ? fortuneSettingsTypeList[0].id
        : null)
  );

  const [emailSendTiming, setEmailSendTiming] = useState<string>(
    currentEmailDelayMinutes > 0 ? "delayed" : "immediate"
  );

  useEffect(() => {
    if (visible) {
      setLocalEmailDelayMinutes(currentEmailDelayMinutes);
      setLocalFortuneSettingsTypeId(
        currentFortuneSettingsTypeId ||
          (fortuneSettingsTypeList.length > 0
            ? fortuneSettingsTypeList[0].id
            : null)
      );
      setEmailSendTiming(
        currentEmailDelayMinutes > 0 ? "delayed" : "immediate"
      );
    }
  }, [
    visible,
    currentEmailDelayMinutes,
    currentFortuneSettingsTypeId,
    fortuneSettingsTypeList,
  ]);

  const handleCloseModal = () => {
    closeModal();
  };

  const handleEmailSendTimingChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setEmailSendTiming(value);
    if (value === "immediate") {
      setLocalEmailDelayMinutes(0);
    } else {
      setLocalEmailDelayMinutes(
        localEmailDelayMinutes > 0 ? localEmailDelayMinutes : 1
      );
    }
  };

  const handleDelayMinutesChange = (
    value: string | number | null | undefined
  ) => {
    if (typeof value === "number") {
      setLocalEmailDelayMinutes(value);
    }
  };

  const handleFortuneSettingsTypeChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setLocalFortuneSettingsTypeId(value);
  };

  const handleSave = () => {
    onSave(localEmailDelayMinutes, localFortuneSettingsTypeId);
  };

  return (
    <CustomModal
      title={i18n.t("survey.emailOptions")}
      okText={i18n.t("common.save")}
      visible={visible}
      closeModal={handleCloseModal}
      onOk={handleSave}
    >
      <Form layout="vertical">
        <Form.Item
          label={i18n.t("survey.emailSendTiming")}
          name="emailSendTiming"
        >
          <Radio.Group
            value={emailSendTiming}
            onChange={handleEmailSendTimingChange}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <Radio value="immediate">
              {i18n.t("survey.emailSendTimingImmediate")}
            </Radio>
            <Radio value="delayed">
              {i18n.t("survey.emailSendTimingDelayed")}
            </Radio>
          </Radio.Group>
          {emailSendTiming === "delayed" && (
            <div
              style={{
                marginLeft: 24,
                marginTop: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              <InputNumber
                min={1}
                max={1440}
                value={localEmailDelayMinutes}
                onChange={handleDelayMinutesChange}
                style={{ width: 80, marginRight: 8 }}
              />
              <span>{i18n.t("survey.emailSendTimingDelayMinutes")}</span>
            </div>
          )}
        </Form.Item>
        <Form.Item
          label={i18n.t("survey.fortuneSettingsType")}
          name="fortuneSettingsType"
        >
          <Radio.Group
            value={localFortuneSettingsTypeId}
            onChange={handleFortuneSettingsTypeChange}
            style={{ display: "flex", flexDirection: "column" }}
          >
            {fortuneSettingsTypeList.map((type: FortuneSettingsType) => (
              <Radio key={type.id} value={type.id}>
                {type.name}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default EmailOptionModal;
